import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useGuild } from '../contexts/GuildContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Skeleton } from '../components/ui/skeleton';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
    DialogFooter,
} from '../components/ui/dialog';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import { Label } from '../components/ui/label';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
    AlertDialogTrigger,
} from '../components/ui/alert-dialog';
import { toast } from 'sonner';
import { 
    Users, 
    Plus, 
    Trash2,
    Shield,
    Crown,
    UserCog
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ROLES = [
    { value: 'admin', label: 'Admin', icon: Crown, color: 'text-amber-500' },
    { value: 'moderator', label: 'Moderator', icon: Shield, color: 'text-blue-500' },
    { value: 'support', label: 'Support', icon: UserCog, color: 'text-green-500' }
];

export default function Staff() {
    const { token } = useAuth();
    const { selectedGuild } = useGuild();
    const [staff, setStaff] = useState([]);
    const [loading, setLoading] = useState(true);
    const [addDialogOpen, setAddDialogOpen] = useState(false);
    const [newStaff, setNewStaff] = useState({
        discord_id: '',
        username: '',
        role: 'support'
    });
    const [adding, setAdding] = useState(false);

    const fetchStaff = async () => {
        if (!selectedGuild || !token) return;
        
        setLoading(true);
        try {
            const response = await axios.get(`${API}/guilds/${selectedGuild}/staff`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setStaff(response.data.staff || []);
        } catch (error) {
            console.error('Error fetching staff:', error);
            toast.error('Failed to load staff');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchStaff();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedGuild, token]);

    const addStaff = async () => {
        if (!newStaff.discord_id || !newStaff.username) {
            toast.error('Please fill all required fields');
            return;
        }

        setAdding(true);
        try {
            await axios.post(`${API}/guilds/${selectedGuild}/staff`, newStaff, {
                headers: { Authorization: `Bearer ${token}` }
            });
            
            toast.success('Staff member added');
            setAddDialogOpen(false);
            setNewStaff({ discord_id: '', username: '', role: 'support' });
            fetchStaff();
        } catch (error) {
            console.error('Error adding staff:', error);
            toast.error(error.response?.data?.detail || 'Failed to add staff');
        } finally {
            setAdding(false);
        }
    };

    const removeStaff = async (staffId) => {
        try {
            await axios.delete(`${API}/guilds/${selectedGuild}/staff/${staffId}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            toast.success('Staff member removed');
            fetchStaff();
        } catch (error) {
            console.error('Error removing staff:', error);
            toast.error('Failed to remove staff');
        }
    };

    const getRoleInfo = (role) => {
        return ROLES.find(r => r.value === role) || ROLES[2];
    };

    if (loading) {
        return (
            <div className="space-y-6" data-testid="staff-loading">
                <div className="flex justify-between items-center">
                    <Skeleton className="h-10 w-48" />
                    <Skeleton className="h-10 w-32" />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {[1, 2, 3].map(i => (
                        <Skeleton key={i} className="h-32" />
                    ))}
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-6" data-testid="staff-page">
            {/* Header */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div>
                    <h1 className="text-2xl font-bold">Staff Management</h1>
                    <p className="text-muted-foreground">Manage your support team members</p>
                </div>
                
                <Dialog open={addDialogOpen} onOpenChange={setAddDialogOpen}>
                    <DialogTrigger asChild>
                        <Button className="flex items-center gap-2" data-testid="add-staff-btn">
                            <Plus className="w-4 h-4" />
                            Add Staff
                        </Button>
                    </DialogTrigger>
                    <DialogContent>
                        <DialogHeader>
                            <DialogTitle>Add Staff Member</DialogTitle>
                            <DialogDescription>
                                Add a Discord user as a staff member for this server
                            </DialogDescription>
                        </DialogHeader>
                        
                        <div className="space-y-4 py-4">
                            <div className="space-y-2">
                                <Label htmlFor="discord_id">Discord User ID</Label>
                                <Input
                                    id="discord_id"
                                    placeholder="e.g., 123456789012345678"
                                    value={newStaff.discord_id}
                                    onChange={e => setNewStaff({ ...newStaff, discord_id: e.target.value })}
                                    data-testid="staff-discord-id-input"
                                />
                                <p className="text-xs text-muted-foreground">
                                    Right-click on a user in Discord and select "Copy User ID"
                                </p>
                            </div>
                            
                            <div className="space-y-2">
                                <Label htmlFor="username">Username</Label>
                                <Input
                                    id="username"
                                    placeholder="e.g., JohnDoe"
                                    value={newStaff.username}
                                    onChange={e => setNewStaff({ ...newStaff, username: e.target.value })}
                                    data-testid="staff-username-input"
                                />
                            </div>
                            
                            <div className="space-y-2">
                                <Label>Role</Label>
                                <Select 
                                    value={newStaff.role}
                                    onValueChange={value => setNewStaff({ ...newStaff, role: value })}
                                >
                                    <SelectTrigger data-testid="staff-role-select">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {ROLES.map(role => (
                                            <SelectItem key={role.value} value={role.value}>
                                                <div className="flex items-center gap-2">
                                                    <role.icon className={`w-4 h-4 ${role.color}`} />
                                                    {role.label}
                                                </div>
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>
                        
                        <DialogFooter>
                            <Button variant="outline" onClick={() => setAddDialogOpen(false)}>
                                Cancel
                            </Button>
                            <Button onClick={addStaff} disabled={adding} data-testid="submit-staff-btn">
                                {adding ? 'Adding...' : 'Add Staff'}
                            </Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {ROLES.map(role => {
                    const count = staff.filter(s => s.role === role.value).length;
                    return (
                        <Card key={role.value}>
                            <CardContent className="p-4 flex items-center gap-4">
                                <div className={`p-3 rounded-xl bg-muted`}>
                                    <role.icon className={`w-6 h-6 ${role.color}`} />
                                </div>
                                <div>
                                    <p className="text-2xl font-bold font-mono">{count}</p>
                                    <p className="text-sm text-muted-foreground">{role.label}s</p>
                                </div>
                            </CardContent>
                        </Card>
                    );
                })}
            </div>

            {/* Staff List */}
            {staff.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {staff.map((member, index) => {
                        const roleInfo = getRoleInfo(member.role);
                        return (
                            <motion.div
                                key={member.id}
                                initial={{ opacity: 0, y: 10 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: index * 0.05 }}
                            >
                                <Card className="card-interactive" data-testid={`staff-${member.id}`}>
                                    <CardContent className="p-4">
                                        <div className="flex items-start justify-between">
                                            <div className="flex items-center gap-3">
                                                <Avatar className="w-12 h-12">
                                                    {member.avatar ? (
                                                        <AvatarImage src={`https://cdn.discordapp.com/avatars/${member.discord_id}/${member.avatar}.png`} />
                                                    ) : null}
                                                    <AvatarFallback>
                                                        {member.username?.charAt(0).toUpperCase()}
                                                    </AvatarFallback>
                                                </Avatar>
                                                <div>
                                                    <p className="font-semibold">{member.username}</p>
                                                    <div className="flex items-center gap-1 text-sm">
                                                        <roleInfo.icon className={`w-3 h-3 ${roleInfo.color}`} />
                                                        <span className={roleInfo.color}>{roleInfo.label}</span>
                                                    </div>
                                                </div>
                                            </div>
                                            
                                            <AlertDialog>
                                                <AlertDialogTrigger asChild>
                                                    <Button 
                                                        variant="ghost" 
                                                        size="icon"
                                                        className="text-muted-foreground hover:text-destructive"
                                                        data-testid={`remove-staff-${member.id}`}
                                                    >
                                                        <Trash2 className="w-4 h-4" />
                                                    </Button>
                                                </AlertDialogTrigger>
                                                <AlertDialogContent>
                                                    <AlertDialogHeader>
                                                        <AlertDialogTitle>Remove Staff Member?</AlertDialogTitle>
                                                        <AlertDialogDescription>
                                                            This will remove {member.username} from the staff team. They will no longer be able to manage tickets.
                                                        </AlertDialogDescription>
                                                    </AlertDialogHeader>
                                                    <AlertDialogFooter>
                                                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                                                        <AlertDialogAction 
                                                            onClick={() => removeStaff(member.id)}
                                                            className="bg-destructive text-destructive-foreground"
                                                        >
                                                            Remove
                                                        </AlertDialogAction>
                                                    </AlertDialogFooter>
                                                </AlertDialogContent>
                                            </AlertDialog>
                                        </div>
                                        
                                        <div className="mt-4 pt-4 border-t flex items-center justify-between text-sm text-muted-foreground">
                                            <span>Tickets handled</span>
                                            <span className="font-mono">{member.tickets_handled || 0}</span>
                                        </div>
                                    </CardContent>
                                </Card>
                            </motion.div>
                        );
                    })}
                </div>
            ) : (
                <Card>
                    <CardContent className="py-12 text-center">
                        <Users className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-50" />
                        <h3 className="text-lg font-semibold mb-1">No staff members yet</h3>
                        <p className="text-muted-foreground mb-4">
                            Add staff members to help manage tickets
                        </p>
                        <Button onClick={() => setAddDialogOpen(true)}>
                            <Plus className="w-4 h-4 mr-2" />
                            Add First Staff Member
                        </Button>
                    </CardContent>
                </Card>
            )}
        </div>
    );
}
